export class NotAuthorized extends Error {
  constructor(message = "Not authorized", { url, method, networkBlocked } = {}) {
    super(message);
    // Override with explicit string to prevent minification
    this.name = "NotAuthorized";
    if (url) this.url = url;
    if (method) this.method = method;
    // True when the 403 response carried none of AWS's identifying headers (see
    // INFRA_REQUEST_ID_HEADER in request.js) — it never reached Plex infrastructure, so this
    // is a client-network intermediary (corporate proxy/firewall) blocking the request, not a
    // real auth/tenant rejection.
    if (networkBlocked) this.networkBlocked = true;
  }
}

// A 401/403 on a v2 workspace query — the caller is authenticated but not
// entitled to this feature. Distinct from NotAuthorized (which RequestBuilder
// throws for the raw HTTP failure) so v2 UI can render "you don't have
// access to this" instead of silently falling through to fixture data,
// which made a real permission error indistinguishable from a genuinely
// empty (but working) project.
export class NotEntitledError extends Error {
  constructor(label = "") {
    super(`Not entitled${label ? `: ${label}` : ""}`);
    this.name = "NotEntitledError";
    this.notEntitled = true;
  }
}

export class ServerError extends Error {
  constructor(error = {message: "Server error"}, status = null, data = null) {
    // Extract message string for super() call
    let message;
    if (typeof(error) === "string") {
      message = error;
    } else {
      message = (error?.message?.indexOf("Failed to fetch") || -1) !== -1 ? "Connection failed" : error.message;
    }

    super(message);

    // Override with explicit string to prevent minification
    this.name = "ServerError";

    // Preserve original stack trace if available
    if (error?.stack) {
      this.stack = error.stack;
    }

    // Store additional data
    this.data = data;
    if (status) {
      this.status = status;
    }

    // Maintains proper stack trace for where our error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  toString() {
    return this.message;
  }
}

export class SearchError extends ServerError {
  constructor(error = "Search failed") {
    super(typeof(error) === "string" ? {message: error} : error);
    // Override with explicit string to prevent minification
    this.name = "SearchError";
  }
}

export class InvalidSearchParameters extends SearchError {
  constructor(params = {}, error = `Invalid search parameters ${JSON.stringify(params)}`) {
    super(error);
    this.params = params;
    // Override with explicit string to prevent minification
    this.name = "InvalidSearchParameters";
  }
}

export class SearchCanceled extends SearchError {
  constructor(error = "Search canceled") {
    super(error);
    // Override with explicit string to prevent minification
    this.name = "SearchCanceled";
  }
}

export class SearchTimeoutError extends SearchError {
  constructor(error = "Search timed out\n"
    + "If you searched with a list of items, please shorten the list and try again\n"
    + "If using compound similarity, raise the similarity threshold") {
    super(error);
    // Override with explicit string to prevent minification
    this.name = "SearchTimeoutError";
  }
}

export class ServerOfflineError extends ServerError {
  constructor(error = "Plex search is currently unavailable, please try again later", { cause } = {}) {
    super(error);
    this.name = "ServerOfflineError";
    if (cause) this.cause = cause;
  }
}

export class ServerStartingException extends ServerError {
  constructor(error = "Server starting up") {
    super(error);
    // Override with explicit string to prevent minification
    this.name = "ServerStartingException";
  }
}

export class CORSError extends ServerError {
  constructor(error = "CORS policy blocked the request. Check server CORS configuration.", details = null) {
    super(error);
    // Override with explicit string to prevent minification
    this.name = "CORSError";
    this.details = details;
  }
}
